import { logReportDownload } from '../../util/auditLogger';
import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { Header } from "../../components/modules";
import { FiFileText } from "react-icons/fi";
import { MdSearch } from "react-icons/md";
import Context from "../../Context";
import { Spinner } from "reactstrap";
import * as xlsx from "xlsx";
import { unixToDateAndTime } from "../../util";

export const ReportLotterieBooksImport = () => {
  const { callApi, configs } = Context();

  const [roundDate, setRoundDate] = useState(configs.roundDate);
  const [allData, setAllData] = useState([]);
  const [displayedData, setDisplayedData] = useState([]);
  const [stat, setStat] = useState({ countLotto: 0, countBook: 0 });
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [building, setBuilding] = useState(false);

  const handleSearch = async () => {
    setAllData([]);
    setDisplayedData([]);
    setSearchText("");
    setLoading(true);

    const data = await callApi(
      "books-getAllBooks",
      {
        queries: [["roundDate", "==", roundDate]],
        limit: 50000,
      },
      { notShowLoading: true },
    );

    const newStat = { countLotto: 0, countBook: data.length };
    data.forEach((book) => {
      newStat.countLotto += (book.count || book.lotteryIds?.length || 0);
    });

    setStat(newStat);
    setAllData(data);
    setDisplayedData(data.slice(0, 100));
    setLoading(false);
  };

  const filteredData = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return allData;
    return allData.filter(
      (book) =>
        book.id?.toLowerCase().includes(q) ||
        book.uploader?.toLowerCase().includes(q),
    );
  }, [allData, searchText]);

  useEffect(() => {
    setDisplayedData(filteredData.slice(0, 100));
  }, [filteredData]);

  const handleLoadMore = () => {
    if (displayedData.length < filteredData.length) {
      const newLimit = Math.min(
        displayedData.length + 100,
        filteredData.length,
      );
      setDisplayedData(filteredData.slice(0, newLimit));
    }
  };

  const uploaderSummary = useMemo(
    () =>
      allData.reduce((acc, book) => {
        const key = book.uploader || "ไม่ระบุ";
        if (!acc[key]) acc[key] = { bookCount: 0, lottoCount: 0 };
        acc[key].bookCount++;
        acc[key].lottoCount += (book.count || book.lotteryIds?.length || 0);
        return acc;
      }, {}),
    [allData],
  );

  const formatBookCreatedAt = (createdAt) => {
    if (!createdAt) return "-";
    const dateStr = unixToDateAndTime(createdAt);
    return dateStr ? dateStr.replace("T", " เวลา ") : "-";
  };

  const handleDownload = () => {
    if (!allData.length) return;

    const listHead = [];
    listHead.push(["", `รายงานสรุปการนำเล่มเข้าระบบ งวด ${roundDate}`]);
    listHead.push(["ชื่อผู้รับผิดชอบ", "จำนวนเล่ม", "จำนวนล็อตเตอรี่"]);

    Object.entries(uploaderSummary).forEach(
      ([uploader, { bookCount, lottoCount }]) => {
        listHead.push([uploader, bookCount, lottoCount]);
      },
    );

    listHead.push([]);
    listHead.push([
      "ลำดับที่",
      `ชื่อเล่ม (${stat.countBook} เล่ม)`,
      `จำนวน (${stat.countLotto} ใบ)`,
      "สร้างเมื่อ",
      "ผู้รับผิดชอบ",
    ]);

    allData.forEach((book, index) => {
      listHead.push([
        index + 1,
        book.id || book.fileNameId || "-",
        book.count || book.lotteryIds?.length || 0,
        formatBookCreatedAt(book.createdAt),
        book.uploader || "-",
      ]);
    });

    setBuilding(true);
    setTimeout(() => {
      try {
        const ws = xlsx.utils.aoa_to_sheet(listHead);
        const wb = xlsx.utils.book_new();
        xlsx.utils.book_append_sheet(wb, ws, "Books");
        logReportDownload();
        xlsx.writeFile(wb, `รายงานการนำเข้าเล่มสลาก_${roundDate}.xlsx`);
      } catch (err) {
        console.error("Export XLSX error:", err);
      } finally {
        setBuilding(false);
      }
    }, 50);
  };

  return (
    <div className="h-fit w-full max-w-[1600px] mb-24">
      <Header
        header="Report"
        subHeader="รายงานการนำเข้าเล่มสลาก"
        icon={<FiFileText className="text-blue" />}
      />

      {/* Search Panel */}
      <div className="relative w-96 bg-white rounded-lg shadow-md mt-4 border border-gray-300 overflow-hidden">
        <div className="p-3 border-b border-gray-300">
          <p className="font-semibold">ค้นหาข้อมูลเล่ม</p>
        </div>
        <div className="relative flex flex-col gap-3 p-3">
          {loading && (
            <div className="z-50 bg-[rgb(255,255,255,0.7)] absolute h-full w-full flex justify-center items-center">
              <Spinner className="w-4 h-4" />
            </div>
          )}
          <div>
            <p className="font-medium text-xs mb-1">งวด</p>
            <select
              className="border-2 w-full h-9 border-gray-300 rounded-lg cursor-pointer disabled:cursor-not-allowed disabled:opacity-65"
              value={roundDate}
              onChange={(e) => setRoundDate(e.target.value)}
              disabled={loading}
            >
              {configs.roundDates
                .slice()
                .sort()
                .reverse()
                .map((rd) => (
                  <option key={rd} value={rd}>
                    {rd}
                  </option>
                ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-1 py-2 px-3 bg-blue text-white text-sm font-semibold rounded-lg shadow-md hover:opacity-60 transition-all duration-200 disabled:opacity-60"
              onClick={handleSearch}
            >
              <MdSearch className="text-white" />
              ค้นหา
            </button>
            <button
              disabled={!allData.length || loading || building}
              className="flex-1 flex items-center justify-center gap-1 py-2 px-3 bg-[rgb(59,176,101)] text-white text-sm font-semibold rounded-lg shadow-md hover:opacity-60 transition-all duration-200 disabled:bg-slate-300 disabled:hover:opacity-100 disabled:cursor-not-allowed"
              onClick={handleDownload}
            >
              {building ? <Spinner className="w-4 h-4" /> : null}
              {building ? "กำลังสร้างไฟล์..." : "ดาวน์โหลด XLSX"}
            </button>
          </div>
          <button
            disabled={loading}
            className="text-xs text-red-500 font-medium underline text-left hover:opacity-70 transition-all disabled:opacity-40"
            onClick={async () => {
              setLoading(true);
              const res = await callApi("books-countAllLotteriesToBooks", { roundDate });
              if (res?.status) {
                await handleSearch();
              } else {
                setLoading(false);
              }
            }}
          >
            นับใหม่ (Re-sync ทั้งงวด)
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {allData.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
            <p className="text-sm font-medium text-gray-600">
              จำนวนเล่มทั้งหมด
            </p>
            <p className="text-2xl font-bold text-blue-600 mt-2">
              {stat.countBook.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-1">เล่ม</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
            <p className="text-sm font-medium text-gray-600">
              จำนวนล็อตเตอรี่ทั้งหมด
            </p>
            <p className="text-2xl font-bold text-green-600 mt-2">
              {stat.countLotto.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-1">ใบ</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-yellow-500">
            <p className="text-sm font-medium text-gray-600">
              จำนวนผู้รับผิดชอบ
            </p>
            <p className="text-2xl font-bold text-yellow-600 mt-2">
              {Object.keys(uploaderSummary).length.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-1">คน</p>
          </div>
        </div>
      )}

      {/* Uploader Breakdown */}
      {allData.length > 0 && (
        <div className="bg-white rounded-lg shadow-md mt-4 border border-gray-300 overflow-hidden">
          <div className="p-3 border-b border-gray-300">
            <p className="font-semibold">สรุปตามผู้รับผิดชอบ</p>
          </div>
          <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(uploaderSummary).map(
              ([uploader, { bookCount, lottoCount }]) => (
                <div
                  key={uploader}
                  className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                >
                  <p className="font-semibold text-sm truncate">{uploader}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {bookCount.toLocaleString()} เล่ม
                  </p>
                  <p className="text-xs text-gray-500">
                    {lottoCount.toLocaleString()} ใบ
                  </p>
                </div>
              ),
            )}
          </div>
        </div>
      )}

      {/* Table */}
      {allData.length > 0 && (
        <div className="relative bg-white w-full rounded-lg shadow-md mt-4 border border-gray-300 overflow-hidden">
          <div className="flex flex-wrap bg-white gap-3 items-center p-3 border-b border-gray-300">
            <p className="font-semibold">รายการเล่มทั้งหมด</p>
            <p className="text-xs font-semibold text-[#969797]">
              {displayedData.length.toLocaleString()} /{" "}
              {filteredData.length.toLocaleString()} รายการ
            </p>
            <div className="ml-auto">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="ค้นหา ชื่อเล่ม, ผู้รับผิดชอบ..."
                className="border border-gray-300 rounded-lg h-8 px-3 text-sm w-56 focus:outline-none focus:border-blue-400"
              />
            </div>
          </div>
          <div
            className="overflow-y-auto"
            style={{ height: "600px" }}
            onScroll={(e) => {
              const { scrollTop, scrollHeight, clientHeight } = e.target;
              if (scrollHeight - scrollTop <= clientHeight + 100) {
                handleLoadMore();
              }
            }}
          >
            <Table className="w-full">
              <TableHeader className="bg-[#f2f3f5] sticky top-0 z-10">
                <TableRow>
                  <TableHead>ลำดับที่</TableHead>
                  <TableHead>ชื่อเล่ม</TableHead>
                  <TableHead className="text-center">จำนวนล็อตเตอรี่</TableHead>
                  <TableHead>สร้างเมื่อ</TableHead>
                  <TableHead>ผู้รับผิดชอบ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedData.map((book, index) => (
                  <TableRow key={book.id}>
                    <TableCell className="font-mono tabular-nums text-center">
                      {index + 1}
                    </TableCell>
                    <TableCell className="font-mono text-xs font-semibold">
                      {book.id || "-"}
                    </TableCell>
                    <TableCell className="text-center font-mono tabular-nums font-semibold">
                      {(book.count || book.lotteryIds?.length || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-xs">
                      {formatBookCreatedAt(book.createdAt)}
                    </TableCell>
                    <TableCell>{book.uploader || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {displayedData.length < filteredData.length && (
            <div className="p-3 text-center text-sm text-gray-500">
              Scroll to load more... ({displayedData.length.toLocaleString()} /{" "}
              {filteredData.length.toLocaleString()})
            </div>
          )}
        </div>
      )}
    </div>
  );
};
